import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { setAppQueryClient } from "./lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";
import { RouteChunkErrorBoundary } from "./components/RouteChunkErrorBoundary";
import { ChunkErrorFallback } from "./components/ChunkErrorFallback";
import { LoadingSurface } from "./components/loading/LoadingSurface";
import { LoadingCoordinatorProvider } from "./contexts/LoadingCoordinatorProvider";
import { DevQueryHud } from "./components/dev/DevQueryHud";
import { BootDebugInit } from "./components/dev/BootDebugInit";
import { RouteChunkLoader } from "./components/ui/route-chunk-loader";
import { AdminLayout } from "./components/layout/AdminLayout";
import { emitFromApiError } from "./lib/auth/sessionCoordinator";
import { releasePreReactBootSplashForAuthRoute } from "./lib/preReactBootHandoff";
import { isPreReactEarlyReleasePath } from "./lib/authPublicPaths";
import { shouldRetryByErrorKind, retryDelayByErrorKind } from "./lib/errorFlow";

// Route-level lazy components (code-split by page)
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthLayout = lazy(() => import("./components/layout/AuthLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const WorkReports = lazy(() => import("./pages/admin/WorkReports"));
const Incidents = lazy(() => import("./pages/admin/Incidents"));
const SizingConfigPage = lazy(() => import("./pages/admin/sizing-config/SizingConfigPage"));
const SizingExecution = lazy(() => import("./pages/admin/SizingExecution"));
const Accounts = lazy(() => import("./pages/admin/Accounts"));
const CreateAccount = lazy(() => import("./pages/admin/CreateAccount"));
const EditAccount = lazy(() => import("./pages/admin/EditAccount"));
const Roles = lazy(() => import("./pages/admin/Roles"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const OverviewTemplates = lazy(() => import("./pages/admin/OverviewTemplates"));
const IncidentsConfig = lazy(() => import("./pages/admin/IncidentsConfig"));
const ServiceTiersTab = lazy(() => import("./pages/admin/incidents-config/ServiceTiersTab"));
const ImpactTypesTab = lazy(() => import("./pages/admin/incidents-config/ImpactTypesTab"));
const ImpactScopesTab = lazy(() => import("./pages/admin/incidents-config/ImpactScopesTab"));
const ConditionalRulesTab = lazy(() => import("./pages/admin/incidents-config/ConditionalRulesTab"));
const KpiPoliciesTab = lazy(() => import("./pages/admin/incidents-config/KpiPoliciesTab"));
const StepTemplatesTab = lazy(() => import("./pages/admin/incidents-config/StepTemplatesTab"));
const SeverityLevelsTab = lazy(() => import("./pages/admin/incidents-config/SeverityLevelsTab"));
const HistoryTab = lazy(() => import("./pages/admin/incidents-config/HistoryTab"));
const Profile = lazy(() => import("./pages/admin/Profile"));
const ProjectList = lazy(() => import("./pages/admin/ProjectList"));
const ProjectAssignment = lazy(() => import("./pages/admin/ProjectAssignment"));
const ShareManagement = lazy(() => import("./pages/admin/ShareManagement"));
const DutySchedules = lazy(() => import("./pages/admin/DutySchedules"));
const SharedAssignmentView = lazy(() => import("./pages/SharedAssignmentView"));
const UniversalPreviewPage = lazy(() => import("./pages/UniversalPreviewPage"));

// Configure QueryClient with default options (Phase 4: reduce refetch-on-focus flicker)
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const source = Array.isArray(query.queryKey)
        ? `react-query:${query.queryKey.join("/")}`
        : "react-query:unknown";
      emitFromApiError(error, source);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute – reduce refetch on mount/focus; critical hooks override
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false, // Critical hooks (e.g. useMyMenu, useThemeQuery) set true
      retry: shouldRetryByErrorKind,
      retryDelay: retryDelayByErrorKind,
    },
  },
});

setAppQueryClient(queryClient);

const AUTH_ROUTE_PREFIXES = ["/login", "/forgot-password", "/reset-password"] as const;

const AppRoutes = () => {
  const location = useLocation();
  const isAuthRoute = AUTH_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  useEffect(() => {
    // Auth forms + /share/:code — release preReact without waiting for AdminLayout settle.
    // Keep LoadingSurface for share (isAuthRoute stays false) so page-data can own content overlay.
    if (isAuthRoute || isPreReactEarlyReleasePath(location.pathname)) {
      releasePreReactBootSplashForAuthRoute(
        isAuthRoute ? 'auth-route-mounted' : 'early-release-route-mounted',
      );
    }
  }, [isAuthRoute, location.pathname]);

  return (
    <>
      {!isAuthRoute && <LoadingSurface />}
      {!isAuthRoute && <DevQueryHud />}
      {!isAuthRoute && <BootDebugInit />}
      <ChunkErrorFallback />
      <RouteChunkErrorBoundary>
        <Suspense fallback={null}>
        <Routes>
        <Route path="/index" element={<Index />} />
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ForgotPassword />} />
        </Route>
        
        {/* Admin Routes - without /admin prefix */}
        <Route path="/" element={<AdminLayout />}>
          {/* Legacy root entry: keep backward compatibility */}
          <Route index element={<Navigate to="/overview" replace />} />
          <Route path="overview" element={<Dashboard />} />
          <Route path="work-reports" element={<WorkReports />} />
          <Route path="incidents" element={<Incidents />} />
          {/* S7.3: legacy utilities page → User Sizing (replace; utilities live in Admin Config tab) */}
          <Route path="optimization" element={<Navigate to="/optimization/sizing" replace />} />
          {/* S7 User Sizing — more specific config route must stay registered before sibling */}
          <Route path="optimization/sizing/config" element={<SizingConfigPage />} />
          <Route path="optimization/sizing" element={<SizingExecution />} />
          {/* Projects menu */}
          <Route path="projects" element={<Navigate to="/projects/list" replace />} />
          <Route path="projects/list" element={<ProjectList />} />
          <Route path="projects/assignment" element={<ProjectAssignment />} />
          <Route path="projects/shares" element={<ShareManagement />} />
          <Route path="projects/duty-schedules" element={<DutySchedules />} />
          {/* Redirect /permissions to first submenu */}
          <Route path="permissions" element={<Navigate to="/permissions/accounts" replace />} />
          <Route path="permissions/accounts" element={<Accounts />} />
          <Route path="permissions/create-account" element={<CreateAccount />} />
          <Route path="permissions/edit-account/:id" element={<EditAccount />} />
          <Route path="permissions/roles" element={<Roles />} />
          <Route path="permissions/delegations" element={<Roles />} />
          {/* Redirect old /settings to new /config/overview (backward compatibility) */}
          <Route path="settings" element={<Navigate to="/config/overview" replace />} />
          {/* New config routes */}
          <Route path="config/overview-templates" element={<OverviewTemplates />} />
          <Route path="config/overview" element={<Settings />} />
          {/* Incidents Config - Consolidated routing (new) */}
          <Route path="incidents-config" element={<IncidentsConfig />}>
            <Route index element={<Navigate to="/incidents-config/service-tiers" replace />} />
            <Route path="service-tiers" element={<ServiceTiersTab />} />
            <Route path="impact-types" element={<ImpactTypesTab />} />
            <Route path="impact-scopes" element={<ImpactScopesTab />} />
            <Route path="kpi-policies" element={<KpiPoliciesTab />} />
            <Route path="conditional-rules" element={<ConditionalRulesTab />} />
            <Route path="step-templates" element={<StepTemplatesTab />} />
            <Route path="severity-levels" element={<SeverityLevelsTab />} />
            <Route path="history" element={<HistoryTab />} />
          </Route>
          <Route path="profile" element={<Profile />} />
        </Route>
        
        {/* Public share route */}
        <Route path="/share/:code" element={<SharedAssignmentView />} />
        
        {/* Universal Preview Page (full screen viewer) */}
        <Route path="/viewer/work-reports/:reportId/attachments/:token" element={<UniversalPreviewPage />} />
        
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </RouteChunkErrorBoundary>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SiteSettingsProvider>
      <ThemeProvider>
        <TooltipProvider>
          <LoadingCoordinatorProvider>
            {/* BrandingDomApplier is already in BootstrapRoot, no need here */}
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </LoadingCoordinatorProvider>
        </TooltipProvider>
      </ThemeProvider>
    </SiteSettingsProvider>
  </QueryClientProvider>
);

export default App;
